import { Info } from 'components/Alert/Info/Info';
import { FormattedAmount } from 'components/FormattedAmount/FormattedAmount';
import { Instructions } from 'components/InstructionUI/InstructionUI';
import { ProgressBar } from 'components/PaymentInstructions/components/ProgressBar/ProgressBar';
import { Button } from 'components/ui/Button/Button';
import { Heading } from 'components/ui/Heading/Heading';
import type { Instruction, Price } from 'models';
import { EmbeddedRepository } from 'repositories/embedded/embedded';
import { RedirectUrl, useTranslations } from 'utils';
import { createMarkup } from 'utils/createMarkup/createMarkup';

const DEFAULT_PAYMENT_STATUS = 'initiated';

function redirectToExternalUrl({ url, id }) {
  const returnUrlWithId = RedirectUrl.build(url, id, DEFAULT_PAYMENT_STATUS);
  const onFinish = EmbeddedRepository.getProp('fwFinish');

  onFinish(returnUrlWithId.toString());
}

export type BillPayInstructionsProps = {
  instructions: Instruction[];
  paymentId: string;
  price: Price;
  recipientName: string;
  onCancel: () => void;
  returnUrl: string;
};

function BillPayInstructions({
  instructions,
  paymentId,
  price,
  recipientName,
  onCancel,
  returnUrl,
}: BillPayInstructionsProps) {
  const i18n = useTranslations();

  const { Instruction, Label, Value } = Instructions;
  const amountToPay = (
    <div className="PaymentInstructions-row" data-testid="amountToPay">
      <Instruction className="Checkout-Instruction">
        <Label className="Checkout-Instruction-label">
          {i18n.t('checkout.instructions.offline.amount.label')}
        </Label>
        <Value className="Checkout-Instruction-value fontWeight-semiBold">
          <FormattedAmount value={price.value} currency={price.currency} />
        </Value>
      </Instruction>
    </div>
  );

  const paymentIdComponent = (
    <div className="PaymentInstructions-row" data-testid="paymentId">
      <Instruction className="Checkout-Instruction">
        <Label className="Checkout-Instruction-label">
          {i18n.t('checkout.instructions.offline.reference.label')}
        </Label>
        <Value
          value={paymentId}
          className="Checkout-Instruction-value fontWeight-semiBold"
        />
      </Instruction>
    </div>
  );
  const paymentInstructions = instructions.map(instruction => (
    <div className="PaymentInstructions-row" key={instruction.name}>
      <Instruction className="Checkout-Instruction">
        <Label className="Checkout-Instruction-label">
          {i18n.t(instruction.label)}
        </Label>
        <Value className="Checkout-Instruction-value fontWeight-semiBold">
          {instruction.value}
        </Value>
      </Instruction>
    </div>
  ));

  return (
    <>
      <Heading as="h2" size="large" className="fontWeight-semiBold">
        {i18n.t('checkout.instructions.offline.title')}
      </Heading>
      <p className="type-regular marginBottom-lg">
        {i18n.t('checkout.instructions.billPay.description')}
      </p>
      <p
        className="type-regular marginBottom-lg"
        dangerouslySetInnerHTML={createMarkup(
          i18n.t('checkout.instructions.billPay.howToPay'),
        )}
      />
      <ProgressBar recipientName={recipientName} />
      <Info>
        <Info.FilledIcon height="14" width="14" className="marginRight-sm" />
        <span className="PaymentInstructions-description">
          {i18n.t('checkout.instructions.billPay.info')}
        </span>
      </Info>
      <div className="Checkout-PaymentInstructions-details">
        <p className="type-regular marginBottom-md PaymentInstructions-bankInstructions">
          {i18n.t('checkout.instructions.bpay.paymentInstructions')}
        </p>
        <hr />
        <div className="PaymentInstructions-list">
          {amountToPay}
          {paymentIdComponent}
          {paymentInstructions}
        </div>
      </div>
      <div className="Instructions-actions">
        <Button
          className="marginTop-xl Offline-Redirect-Button"
          labelClassName="normal-case leading-4"
          tone="primary"
          size="full"
          data-testid="closeCheckout"
          onClick={() =>
            redirectToExternalUrl({ url: returnUrl, id: paymentId })
          }
          dangerouslySetInnerHTML={createMarkup(
            i18n.t('checkout.instructions.offline.closeCheckout'),
          )}
        />
        <button onClick={onCancel} className="Instructions-actions--cancel">
          {i18n.t('checkout.cancel.action')}
        </button>
      </div>
    </>
  );
}

export { BillPayInstructions };
